import React, { useState, useEffect, useRef, useCallback } from 'react';
import ProjectVisual from './ProjectVisual';

/* "#06B6D4" -> "6, 182, 212" so the accent can be reused at any opacity, plus
   --accent-fg: the readable ink for text sitting *on* a solid accent fill.
   Dark accents (slate, deep blue) need white text; bright ones need near-black. */
const accentVars = (hex = '#22D3EE') => {
  const value = hex.replace('#', '');
  const int = parseInt(value.length === 3 ? value.replace(/(.)/g, '$1$1') : value, 16);
  const channels = [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  const rgb = channels.join(', ');

  // WCAG relative luminance; above ~0.179 black text beats white text
  const [r, g, b] = channels.map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return {
    '--accent': hex,
    '--accent-rgb': rgb,
    '--accent-fg': luminance > 0.179 ? '#07070b' : '#ffffff'
  };
};

const SLIDE_MS = 3500;

/* Cross-fading product screenshots — used where a live iframe is a poor
   experience (login walls, layouts that don't fit the frame). */
const ScreenshotCarousel = ({ shots, intervalMs = SLIDE_MS }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (shots.length < 2) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % shots.length), intervalMs);
    return () => clearInterval(timer);
  }, [shots.length, intervalMs]);

  return (
    <div className="proj-shots">
      {shots.map((shot, i) => (
        <img
          key={shot.src}
          src={`${process.env.PUBLIC_URL}${shot.src}`}
          alt={shot.alt || ''}
          loading={i === 0 ? 'eager' : 'lazy'}
          className={`proj-shot ${i === index ? 'is-active' : ''}`}
        />
      ))}

      {shots.length > 1 && (
        <div className="proj-shot-dots">
          {shots.map((shot, i) => (
            <button
              key={shot.src}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show screenshot ${i + 1}`}
              className={`proj-shot-dot ${i === index ? 'is-active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* Right-hand panel of the expanded card: shows the real site in an iframe when a
   demo URL exists, falling back to the schematic panel if the site is slow to
   wake up (free hosting) or refuses to be framed. */
const LivePreview = ({ project }) => {
  const shots = project.screenshots || [];
  const canEmbed = shots.length === 0 && Boolean(project.demo) && project.embeddable !== false;
  const initialStatus = () => {
    if (shots.length) return 'shots';
    if (!project.demo) return 'static';
    return canEmbed ? 'loading' : 'unavailable';
  };
  const [status, setStatus] = useState(initialStatus);

  useEffect(() => {
    setStatus(initialStatus());
    if (!canEmbed) return undefined;
    // free hosting can take a while to cold-start; give up after 40s
    const timer = setTimeout(() => {
      setStatus((current) => (current === 'loading' ? 'unavailable' : current));
    }, 40000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.demo, project.title, canEmbed]);

  const host = project.demo ? project.demo.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'local build';

  return (
    <div className="proj-browser">
      {/* Browser chrome */}
      <div className="proj-browser-bar">
        <span className="proj-dot" style={{ background: '#ff5f57' }}></span>
        <span className="proj-dot" style={{ background: '#febc2e' }}></span>
        <span className="proj-dot" style={{ background: '#28c840' }}></span>
        <div className="proj-url">
          <i className="fas fa-lock text-[9px] opacity-60"></i>
          <span className="truncate">{host}</span>
        </div>
        {project.demo && (
          <a
            href={project.demo}
            target="_blank"
            rel="noopener noreferrer"
            className="proj-browser-open"
            title="Open in a new tab"
          >
            <i className="fas fa-arrow-up-right-from-square"></i>
          </a>
        )}
      </div>

      {/* Viewport */}
      <div className="proj-browser-view">
        <div className={`proj-poster ${status === 'ready' || status === 'shots' ? 'opacity-0' : 'opacity-100'}`}>
          <ProjectVisual project={project} variant="poster" />
        </div>

        {status === 'shots' && <ScreenshotCarousel shots={shots} intervalMs={project.slideMs} />}

        {canEmbed && status !== 'unavailable' && (
          <iframe
            key={project.demo}
            src={project.demo}
            title={`${project.title} live preview`}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            className={`proj-frame ${status === 'ready' ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setStatus('ready')}
          />
        )}

        {/* Overlays */}
        {status === 'loading' && (
          <div className="proj-browser-overlay">
            <i className="fas fa-circle-notch fa-spin"></i>
            <span>Waking up the live demo…</span>
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="proj-browser-overlay-link">
              Open it in a new tab <i className="fas fa-arrow-right"></i>
            </a>
          </div>
        )}
        {status === 'unavailable' && (
          <div className="proj-browser-overlay">
            <i className="fas fa-up-right-from-square"></i>
            <span>This site can’t be embedded in a frame</span>
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="proj-browser-overlay-link">
              Open the live site <i className="fas fa-arrow-right"></i>
            </a>
          </div>
        )}
        {status === 'static' && (
          <div className="proj-browser-overlay proj-browser-overlay--soft">
            <i className="fab fa-github"></i>
            <span>Source-only project — no hosted demo</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* The expanded card that takes the place of a row, in-line with the list */
const ProjectDetail = ({ project, index, onClose }) => (
  <div className="proj-detail" style={accentVars(project.accent)}>
    <button type="button" className="proj-close" onClick={onClose} aria-label="Collapse project">
      <i className="fas fa-xmark"></i>
    </button>

    <div className="proj-detail-grid">
      {/* Left: the story */}
      <div className="proj-detail-info">
        <div className="proj-detail-top">
          <span className="proj-detail-num">{String(index + 1).padStart(2, '0')}</span>
          <span className="proj-detail-rule"></span>
          <span className="proj-eyebrow">
            <span className="proj-eyebrow-dot"></span> {project.category}
          </span>
        </div>

        <h3 className="proj-detail-title">{project.title}</h3>
        <p className="proj-detail-desc">{project.description}</p>

        {project.highlights?.length > 0 && (
          <div className="proj-chip-row">
            {project.highlights.map((h) => (
              <span key={h.label} className="proj-chip">
                <i className={h.icon}></i>
                {h.label}
              </span>
            ))}
          </div>
        )}

        <div className="proj-tag-row">
          {project.technologies?.map((tech) => (
            <span key={tech} className="proj-tag">
              {tech}
            </span>
          ))}
        </div>

        <div className="proj-cta-row">
          {project.demo && (
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="proj-btn proj-btn--solid">
              <i className="fas fa-arrow-up-right-from-square"></i>
              Live Demo
            </a>
          )}
          <a href={project.github} target="_blank" rel="noopener noreferrer" className="proj-btn proj-btn--ghost">
            <i className="fab fa-github"></i>
            View Code
          </a>
        </div>
      </div>

      {/* Right: the live site */}
      <div className="proj-detail-preview">
        <LivePreview project={project} />
      </div>
    </div>
  </div>
);

const Projects3D = ({ data }) => {
  const { projects = [] } = data;
  const [activeIndex, setActiveIndex] = useState(null);
  const itemRefs = useRef([]);

  const close = useCallback(() => setActiveIndex(null), []);

  // Esc collapses whatever is open
  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, close]);

  const toggle = (index) => {
    if (index === activeIndex) {
      close();
      return;
    }
    setActiveIndex(index);
    // nudge the freshly expanded card into view without jumping the whole page
    requestAnimationFrame(() => {
      itemRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  return (
    <section id="projects" className="proj-section py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-blue-500"></div>
            <span className="text-blue-400">My Work</span>
            <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-blue-500"></div>
          </div>
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-subtitle">
            Click a project to expand its details and live preview
          </p>
        </div>

        <div className="proj-list max-w-6xl mx-auto">
          {projects.map((project, index) => {
            const isOpen = index === activeIndex;

            return (
              <div
                key={project.title}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={`proj-item animate-fade-in-up ${isOpen ? 'is-open' : ''}`}
                style={{ ...accentVars(project.accent), animationDelay: `${index * 0.06}s` }}
              >
                {isOpen ? (
                  <ProjectDetail project={project} index={index} onClose={close} />
                ) : (
                  <button type="button" onClick={() => toggle(index)} className="proj-row" aria-expanded="false">
                    {project.award && (
                      <span className="proj-row-award" title={project.award}>
                        <i className="fas fa-trophy" aria-hidden="true"></i>
                        <span className="sr-only">{project.award}</span>
                      </span>
                    )}

                    <span className="proj-row-num">{String(index + 1).padStart(2, '0')}</span>

                    <span className="proj-row-head">
                      <span className="proj-row-cat">{project.category}</span>
                      <span className="proj-row-title">{project.title}</span>
                    </span>

                    <span className="proj-row-desc">{project.tagline || project.description}</span>

                    <span className="proj-row-thumb">
                      <ProjectVisual project={project} variant="thumb" />
                      {project.demo && <span className="proj-row-live">LIVE</span>}
                    </span>

                    <span className="proj-row-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* View All Projects */}
        <div className="text-center mt-16">
          <p className="text-slate-600 mb-6">
            Want to see more of my work? Check out my complete portfolio on GitHub.
          </p>
          <a
            href="https://github.com/ADHIRAJ00000"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-700 transition-colors"
          >
            View All Projects
            <i className="fas fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Projects3D;
